import { readSessionKey } from '../../api/queryCache';
import { createContext, useContext, useLayoutEffect, useRef, useEffect, useState, useCallback, Dispatch, SetStateAction, lazy, ComponentType } from 'react';
import { useRouteActive } from './runtime';
import { subscribeDataChanges } from '../../api/dataSync';

export type ReadScope = { set: (owner: object, pending: boolean) => void };
export const ReadinessContext = createContext<ReadScope | null>(null);
export type PrefetchTask = { key: string; read: (signal: AbortSignal) => Promise<unknown>; priority?: number };
type WarmTask = PrefetchTask & { session: string | null; remaining: number };
const foreground = new Set<object>();
const queue = new Map<object, WarmTask[]>();
let running: { owner: object; key: string; controller: AbortController; task: WarmTask; started: number; suspended: boolean } | undefined;
let scheduled: ReturnType<typeof setTimeout> | undefined;
const mayWarm = () => typeof document !== 'undefined' && document.visibilityState !== 'hidden'
    && !(navigator as Navigator & {connection?: {saveData?: boolean}}).connection?.saveData;
function pump() {
    if (scheduled || running || foreground.size || !mayWarm()) return;
    // Yield the main thread to the completed foreground render. No minimum display delay.
    scheduled = setTimeout(() => {
        scheduled = undefined;
        if (running || foreground.size || !mayWarm()) return;
        const entry = [...queue.entries()].sort((a,b)=>(b[1][0]?.priority ?? 0)-(a[1][0]?.priority ?? 0))[0];
        if (!entry) return;
        const [owner, tasks] = entry; const task = tasks.shift();
        if (!tasks.length) queue.delete(owner);
        if (!task || task.session !== readSessionKey()) { pump(); return; }
        const controller = new AbortController(); const job = {owner,controller,key:task.key,task,started:Date.now(),suspended:false}; running = job;
        const timeout = setTimeout(() => controller.abort(), task.remaining);
        const stopped = new Promise<never>((_, reject) => controller.signal.addEventListener('abort', () => reject(new DOMException('Prefetch cancelled', 'AbortError')), {once:true}));
        void Promise.race([stopped, Promise.resolve().then(() => controller.signal.aborted ? undefined : task.read(controller.signal))])
            .catch(() => {}).finally(() => { clearTimeout(timeout); if(running===job)running=undefined; pump(); });
    }, 0);
}
// Preemption pauses still-relevant work. A cancelled source, failure or timeout never retries.
function suspendPrefetch() {
    const job=running;
    if(!job || job.controller.signal.aborted || job.suspended)return;
    job.suspended=true;
    const remaining=job.task.remaining-(Date.now()-job.started);
    if(remaining>0)queue.set(job.owner,[{...job.task,remaining},...(queue.get(job.owner) ?? [])]);
    job.controller.abort();
}
/** Foreground readers always displace speculative work; unrelated mounted shells never wait for it. */
export function beginForegroundRead(owner: object) {
    foreground.add(owner); suspendPrefetch();
    return () => {foreground.delete(owner);pump();};
}
export function enqueuePrefetch(tasks: readonly PrefetchTask[]) {
    const owner = {};
    const keys = new Set(Array.from(queue.values()).flat().map(task => task.key));
    if (running) keys.add(running.key);
    const session = readSessionKey();
    const available = Math.max(0, 8 - keys.size);
    const accepted = tasks.filter(task => !keys.has(task.key) && !!keys.add(task.key)).slice(0, available)
        .map(task=>({...task,session,remaining:5000})).sort((a,b)=>(b.priority ?? 0)-(a.priority ?? 0));
    if (accepted.length) {
        queue.set(owner, accepted);
        if(running && (accepted[0].priority ?? 0)>(running.task.priority ?? 0))suspendPrefetch();
        pump();
    }
    return () => {
        queue.delete(owner);
        if (!queue.size && scheduled) {clearTimeout(scheduled);scheduled=undefined;}
        if(running?.owner===owner)running.controller.abort();
    };
}
subscribeDataChanges(change => {
    if (change.reason !== 'focus') { queue.clear(); if(scheduled){clearTimeout(scheduled);scheduled=undefined;} running?.controller.abort(); }
});
if (typeof document !== 'undefined') document.addEventListener('visibilitychange', () => {
    if(document.visibilityState==='hidden')suspendPrefetch();else pump();
});
/** Report initial/identity-dependent reads before paint; cached refreshes do not hide existing content. */
export function useCriticalRead(pending: boolean) {
    const scope = useContext(ReadinessContext); const owner = useRef({}); const active = useRouteActive();
    pending = pending && active;
    useLayoutEffect(() => {scope?.set(owner.current,pending);return () => scope?.set(owner.current,false);},[scope,pending]);
    useLayoutEffect(() => pending ? beginForegroundRead(owner.current) : undefined,[pending]);
}
/** Only explicit bounded read descriptors may warm; never mount hidden editors to discover their reads. */
export function useAdjacentPrefetch(ready: boolean, tasks: readonly PrefetchTask[]) {
    const active = useRouteActive(); const latest = useRef(tasks); latest.current=tasks;
    const key = tasks.map(task=>`${task.key}:${task.priority ?? 0}`).join('\n');
    useEffect(() => ready && active ? enqueuePrefetch(latest.current) : undefined,[ready,active,key]);
}

/** Bridge legacy read flags into the same boundary, including reads started in passive effects.
 * Do not use for uploads, payments or write progress; those retain their action feedback.
 */
export function useReadState(initial: boolean | (() => boolean), enabled = true): [boolean, Dispatch<SetStateAction<boolean>>] {
    const [pending, setPending] = useState(initial);
    const scope = useContext(ReadinessContext);
    const active = useRouteActive();
    const owner = useRef({});
    const current = useRef(pending); current.current = pending;
    const live = useRef({scope, enabled: enabled && active}); live.current = {scope, enabled:enabled && active};
    useLayoutEffect(() => pending && enabled && active ? beginForegroundRead(owner.current) : undefined, [pending,enabled,active]);
    useLayoutEffect(() => {scope?.set(owner.current, pending && enabled && active);return () => scope?.set(owner.current, false);}, [scope,pending,enabled,active]);
    const set = useCallback<Dispatch<SetStateAction<boolean>>>((value) => {
        const next = typeof value === 'function' ? value(current.current) : value;
        current.current = next;
        live.current.scope?.set(owner.current, next && live.current.enabled);
        setPending(next);
    }, []);
    return [pending, set];
}

/** Code-only warming shares the same loader as React.lazy; importing never mounts the view. */
export function lazyReadView<T extends ComponentType<any>>(load:()=>Promise<{default:T}>) {
    let pending:Promise<{default:T}>|undefined;
    const preload=()=>pending ?? (pending=load().catch(error=>{pending=undefined;throw error;}));
    return Object.assign(lazy(preload),{preload});
}
export function useAdjacentViews(current:string|null|undefined, views:readonly {key:string;component:unknown}[]) {
    const index=views.findIndex(view=>view.key===current);
    const tasks=index<0?[]:views.slice(index+1,index+3).flatMap(view=>{
        const component=view.component as {preload?:()=>Promise<unknown>};
        return component?.preload?[{key:`code|${view.key}`,priority:1,read:()=>component.preload!()}]:[];
    });
    useAdjacentPrefetch(true,tasks);
}
